import type { UIMessage } from "ai";
import { useEffect, useState } from "react";

import { logger } from "~/libs/logger";
import { mastraClient } from "~/libs/mastra.lib";

const AGENT_ID = "supervisor-agent";

export type ThreadSubscriptionStatus = "idle" | "streaming" | "done" | "error";

type ToolPart = {
  type: `tool-${string}`;
  toolCallId: string;
  state: "input-available" | "output-available" | "output-error";
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

type LivePart =
  | { type: "text"; text: string }
  | { type: "reasoning"; text: string; state: "streaming" | "done" }
  | ToolPart;

/**
 * Minimal local view of the Mastra durable-agent chunks we render. The full
 * `ChunkType` lives in `@mastra/core` (not a web dependency), so we narrow to
 * the handful of fields the live view consumes and cast the SDK chunk to this.
 */
type LiveChunk =
  | { type: "text-start"; payload: { id: string } }
  | { type: "text-delta"; payload: { id: string; text: string } }
  | { type: "reasoning-start"; payload: { id: string } }
  | { type: "reasoning-delta"; payload: { id: string; text: string } }
  | { type: "reasoning-end"; payload: { id: string } }
  | {
      type: "tool-call";
      payload: { toolCallId: string; toolName: string; args?: unknown };
    }
  | { type: "tool-result"; payload: { toolCallId: string; result?: unknown } }
  | { type: "tool-error"; payload: { toolCallId: string; error?: unknown } };

/**
 * Accumulates a Mastra durable-agent chunk stream into a single assistant
 * {@link UIMessage} whose parts mirror what the normal streaming chat path
 * produces, so the read-only generating view renders identically to a live
 * turn (text, reasoning, tool calls). Returns `true` when a chunk changed the
 * rendered message so the caller can re-snapshot.
 */
const createMessageBuilder = (id: string) => {
  const parts: LivePart[] = [];
  const textById = new Map<string, { type: "text"; text: string }>();
  const reasoningById = new Map<
    string,
    { type: "reasoning"; text: string; state: "streaming" | "done" }
  >();
  const toolByCallId = new Map<string, ToolPart>();

  const apply = (chunk: LiveChunk): boolean => {
    switch (chunk.type) {
      case "text-start": {
        const { id: partId } = chunk.payload;
        if (!textById.has(partId)) {
          const part = { type: "text" as const, text: "" };
          textById.set(partId, part);
          parts.push(part);
        }
        return false;
      }
      case "text-delta": {
        const { id: partId, text } = chunk.payload;
        let part = textById.get(partId);
        if (!part) {
          part = { type: "text", text: "" };
          textById.set(partId, part);
          parts.push(part);
        }
        part.text += text;
        return true;
      }
      case "reasoning-start": {
        const { id: partId } = chunk.payload;
        if (!reasoningById.has(partId)) {
          const part = {
            type: "reasoning" as const,
            text: "",
            state: "streaming" as const,
          };
          reasoningById.set(partId, part);
          parts.push(part);
        }
        return true;
      }
      case "reasoning-delta": {
        const { id: partId, text } = chunk.payload;
        let part = reasoningById.get(partId);
        if (!part) {
          part = { type: "reasoning", text: "", state: "streaming" };
          reasoningById.set(partId, part);
          parts.push(part);
        }
        part.text += text;
        return true;
      }
      case "reasoning-end": {
        const part = reasoningById.get(chunk.payload.id);
        if (part) part.state = "done";
        return true;
      }
      case "tool-call": {
        const { toolCallId, toolName, args } = chunk.payload;
        let part = toolByCallId.get(toolCallId);
        if (!part) {
          part = {
            type: `tool-${toolName}`,
            toolCallId,
            state: "input-available",
          };
          toolByCallId.set(toolCallId, part);
          parts.push(part);
        }
        part.input = args;
        part.state = "input-available";
        return true;
      }
      case "tool-result": {
        const { toolCallId, result } = chunk.payload;
        const part = toolByCallId.get(toolCallId);
        if (part) {
          part.output = result;
          part.state = "output-available";
          return true;
        }
        return false;
      }
      case "tool-error": {
        const { toolCallId, error } = chunk.payload;
        const part = toolByCallId.get(toolCallId);
        if (part) {
          part.state = "output-error";
          part.errorText =
            error instanceof Error ? error.message : String(error);
          return true;
        }
        return false;
      }
      default:
        return false;
    }
  };

  const snapshot = (): UIMessage =>
    ({
      id,
      role: "assistant",
      parts: parts.map((p) => ({ ...p })),
    }) as unknown as UIMessage;

  const hasParts = () => parts.length > 0;

  return { apply, snapshot, hasParts };
};

/**
 * Live-tails a durable supervisor run via `observe({ runId })`, accumulating
 * the in-progress assistant turn into a single {@link UIMessage}. Used by the
 * read-only "generating" view so a user returning from onboarding sees the
 * agent thinking + calling tools.
 *
 * `observe` is the durable-native reconnect: it replays the run's cached chunks
 * (from the Mastra `InMemoryServerCache`) and then tails live ones. We use it
 * instead of `subscribeToThread` because fire-and-forget evented runs never
 * register with the thread-stream runtime, so a thread subscription would never
 * see them. The `runId` is persisted on the thread metadata when the background
 * run starts.
 *
 * `status` starts `idle`, flips to `streaming` on the first chunk, and settles
 * on `done`/`error` when the run finishes. When `runId` is absent (the run
 * already finished and its cache expired, or there is no run), it settles
 * `done` immediately so the parent falls through to the recalled transcript.
 */
export const useThreadSubscription = (
  runId: string | null,
  enabled: boolean,
) => {
  const [message, setMessage] = useState<UIMessage | null>(null);
  const [status, setStatus] = useState<ThreadSubscriptionStatus>("idle");

  useEffect(() => {
    if (!enabled) return;
    if (!runId) {
      setStatus("done");
      return;
    }

    let cancelled = false;
    let settled = false;
    const builder = createMessageBuilder(`live-${runId}`);

    setMessage(null);
    setStatus("idle");

    void (async () => {
      try {
        const response = await mastraClient
          .getAgent(AGENT_ID)
          .observe({ runId });
        if (cancelled) return;
        await response.processDataStream({
          onChunk: (chunk) => {
            if (cancelled) return;
            const type = (chunk as { type?: string }).type;
            if (type === "finish" || type === "abort") {
              settled = true;
              setStatus("done");
              return;
            }
            if (type === "error") {
              settled = true;
              setStatus("error");
              return;
            }
            const changed = builder.apply(chunk as unknown as LiveChunk);
            if (changed) {
              setStatus("streaming");
              setMessage(builder.snapshot());
            }
          },
        });
        // Stream closed without an explicit finish/error chunk.
        if (!cancelled && !settled) setStatus("done");
      } catch (err) {
        if (!cancelled) {
          logger.error({ err }, "Failed to observe generating run");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [runId, enabled]);

  return { message, status };
};
